//frontend/src/DimensionEditor.tsx 
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getResourceCalendarFeedUrl } from "./api/CalendarClient";
import { useRef, useState, useEffect, useMemo } from "react";
import { Dimension, DimensionType, Point, Relation, SyntaxchecksData, Tag, Visual } from "./AppTypes";
import { ExtendedCondition } from "./ConditionEditor";
import { notifyProjectActivity } from "./api/pingClient";
import { applyVisuals, getBaseColor, Mode } from "./colorUtils";

// Muss mit backend/settings.py SPECIAL_CHARACTERS synchron bleiben
const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

function isResourceDimension(dim: Dimension): boolean {
  return String(dim.dimType || "").startsWith("resource.");
}

function tryCopyTextWithExecCommand(text: string): boolean {
  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "");
  input.style.position = "absolute";
  input.style.left = "-9999px";
  document.body.appendChild(input);
  input.focus();
  input.select();
  input.setSelectionRange(0, input.value.length);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(input);
  }

  return copied;
}

async function copyTextToClipboard(text: string): Promise<void> {
  // Try the synchronous path first so we keep the original click gesture alive.
  if (tryCopyTextWithExecCommand(text)) {
    return;
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // If the async clipboard API rejects, we fall through to the explicit error.
  }

  throw new Error("Clipboard copy failed");
}

/* ---------- Sortable Tag Card ---------- */
/**
 * Darstellung einer einzelnen Tag-Karte innerhalb einer Dimension:
 * - sortierbar per Drag & Drop (dnd-kit)
 * - Name bearbeitbar
 * - Visual-Effekt (lightness/chroma/hue/default) konfigurierbar
 * - Duplikatswarnung für Namen
 */
function SortableTagCard({
  tag,
  allNames,
  onDelete,
  onUpdate,
  userHue,
  mode,
}: {
  tag: Tag;
  allNames: string[];
  onDelete: (id: string) => void;
  onUpdate: (id: string, field: keyof Tag, value: any) => void;
  userHue: number;
  mode: Mode;
}) {
  // useSortable macht dieses Tag-Element in seiner Liste sortierbar
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: tag.id });

  // Hintergrundfarbe aus Visual-Konfiguration ableiten
  const bg = applyVisuals([tag.visual], mode);

  // prüfen, ob Tag-Name mehrfach vorkommt
  const isDuplicate = allNames.filter((n) => n === tag.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);

  return (
    <div
      ref={setNodeRef}
      className="dimension-card"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: bg,
      }}
    >
      {/* Header: Drag-Handle, Name-Input, Delete-Button */}
      <div className="dimension-card__header">
        {/* Griff zum Ziehen der Karte */}
        <div {...attributes} {...listeners} className="dimension-card__drag" title="Drag to reorder · Drag out to remove">
          ☰
        </div>

        {/* Name - Doppelklick zum Editieren */}
        {isEditingName ? (
          <>
            <input
              type="text"
              value={tag.name}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                onUpdate(tag.id, "name", stripped);
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e) => e.target.select()}
              className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
              style={{ flex: 1 }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em", whiteSpace: "nowrap" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </>
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {tag.name}
          </div>
        )}

        {/* Tag löschen – entfernt per Drag-out */}
      </div>

      {/* Warnhinweis bei doppeltem Namen */}
      {isDuplicate && (
        <div className="dimension-card__warning">
          Name already in use
        </div>
      )}

      {/* Auswahl der Visual-Konfiguration */}
      <div className="dimension-card__section">
        <label>
          Visual:{" "}
          <select
            value={tag.visual.type}
            onChange={(e) =>
              onUpdate(tag.id, "visual", {
                type: e.target.value as Visual["type"],
                value: tag.visual.value ?? 0,
              })
            }
          >
            <option value="default">default</option>
            <option value="lightness">lightness</option>
            <option value="chroma">chroma</option>
            <option value="hue">hue</option>
          </select>
        </label>

        {/* Wert-Input für Visual (je nach Typ anderer Wertebereich) */}
        {tag.visual.type !== "default" && (
          <input
            type="number"
            value={tag.visual.value ?? 0}
            min={tag.visual.type === "hue" ? 0 : -20}
            max={tag.visual.type === "hue" ? 359 : 20}
            onChange={(e) =>
              onUpdate(tag.id, "visual", {
                ...tag.visual,
                value: parseInt(e.target.value),
              })
            }
            className="dimension-card__number"
          />
        )}

      </div>
    </div>
  );
}

/* ---------- Sortable Point Card ---------- */
/**
 * Darstellung einer einzelnen Point-Karte innerhalb einer Dimension:
 * - sortierbar per Drag & Drop
 * - Name bearbeitbar
 * - Visual-Effekt konfigurierbar
 * - Zuordnung zu Tags (tagRefs) per Checkbox-Liste
 * - Duplikatswarnung bei gleichem Namen
 */
function SortablePointCard({
  point,
  allTags,
  allNames,
  onDelete,
  onUpdate,
  userHue,
  mode,
  isTimeWeek,
  isTimeDay,
  isTimeSlot,
  // new props for calendar link copying
  isResource,
  dimName,
  projectId,
}: {
  point: Point;
  allTags: Tag[];
  allNames: string[];
  onDelete: (id: string) => void;
  onUpdate: (id: string, field: keyof Point, value: any) => void;
  userHue: number;
  mode: Mode;
  isTimeWeek?: boolean;
  isTimeDay?: boolean;
  isTimeSlot?: boolean;
  isResource?: boolean;
  dimName?: string;
  projectId?: number;
}) {
  // sortierbare Point-Karte
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: point.id });

  const bg = applyVisuals([point.visual], mode);
  const isDuplicate = allNames.filter((n) => n === point.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  /**
   * Tag-Referenz toggeln:
   * - falls Tag schon enthalten -> entfernen
   * - sonst -> hinzufügen
   */
  const toggleTagRef = (tagId: string) => {
    if (point.tagRefs.includes(tagId)) {
      onUpdate(
        point.id,
        "tagRefs",
        point.tagRefs.filter((t) => t !== tagId)
      );
    } else {
      onUpdate(point.id, "tagRefs", [...point.tagRefs, tagId]);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className="dimension-card"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: bg,
      }}
    >
      {/* Header: Drag-Handle, Name-Input, Delete */}
      <div className="dimension-card__header">
        <div
          {...attributes}
          {...listeners}
          className="dimension-card__drag"
          title="Drag to reorder · Drag out to remove"
        >
          ☰
        </div>

        {/* Name - Doppelklick zum Editieren */}
        {isEditingName ? (
          <>
            <input
              type="text"
              value={point.name}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                onUpdate(point.id, "name", stripped);
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e) => e.target.select()}
              className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
              style={{ flex: 1 }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em", whiteSpace: "nowrap" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </>
        ) : (
          <>
            <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
              {point.name}
            </div>
            {/* Calendar ICS copy button (only for resource dimensions) */}
            {isResource && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  type="button"
                  title="Copy ICS link for this resource"
                  onClick={async (e) => {
                    e.stopPropagation();
                    setCopyError(null);
                    try {
                      if (!projectId || !dimName) {
                        throw new Error("Resource feed is unavailable");
                      }

                      const link = getResourceCalendarFeedUrl(projectId, dimName, point.name);
                      await copyTextToClipboard(link);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    } catch (err: any) {
                      console.error("copy ics link failed:", err);
                      setCopyError(String(err?.message ?? err));
                      setTimeout(() => setCopyError(null), 4000);
                    }
                  }}
                  style={{ marginLeft: 8 }}
                >
                  📅
                </button>
                {copied && <span style={{ marginLeft: 6, color: "#2a2" }}>copied</span>}
                {copyError && <span style={{ marginLeft: 6, color: "#e55" }}>{copyError}</span>}
              </div>
            )}
          </>
        )}

        {/* Point löschen – entfernt per Drag-out */}
      </div>

      {/* Warnhinweis bei doppeltem Namen */}
      {isDuplicate && (
        <div className="dimension-card__warning">
          Name already in use
        </div>
      )}

      {/* Visual-Konfiguration */}
      <div className="dimension-card__section">
        <label>
          Visual:{" "}
          <select
            value={point.visual.type}
            onChange={(e) =>
              onUpdate(point.id, "visual", {
                type: e.target.value as Visual["type"],
                value: point.visual.value ?? 0,
              })
            }
          >
            <option value="default">default</option>
            <option value="lightness">lightness</option>
            <option value="chroma">chroma</option>
            <option value="hue">hue</option>
          </select>
        </label>
        {point.visual.type !== "default" && (
          <input
            type="number"
            value={point.visual.value ?? 0}
            min={point.visual.type === "hue" ? 0 : -20}
            max={point.visual.type === "hue" ? 359 : 20}
            onChange={(e) =>
              onUpdate(point.id, "visual", {
                ...point.visual,
                value: parseInt(e.target.value),
              })
            }
            className="dimension-card__number"
          />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
          {isTimeWeek && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={!!point.template}
                onChange={(e) => onUpdate(point.id, "template", e.target.checked || undefined)}
              />
              <span>template</span>
            </label>
          )}
        </div>
      </div>

  {/* start_date + template für time.period-Points */}
      {isTimeWeek && (
        <div className="dimension-card__section">
          <label className="attr-label">
            <span>start_date:</span>
            <input
              type="date"
              value={point.startDate ?? ""}
              onChange={(e) => onUpdate(point.id, "startDate", e.target.value || undefined)}
            />
          </label>
        </div>
      )}

      {/* day_of_period für time.day-Points */}
      {isTimeDay && (
        <div className="dimension-card__section">
          <label className="attr-label">
            <span>day_of_period:</span>
            <input
              type="number"
              value={point.dayOfPeriod ?? ""}
              onChange={(e) =>
                onUpdate(
                  point.id,
                  "dayOfPeriod",
                  e.target.value ? parseInt(e.target.value, 10) : undefined
                )
              }
              style={{ width: "4.5em" }}
            />
          </label>
        </div>
      )}

      {/* start_time / duration_minutes für time.slot-Points */}
      {isTimeSlot && (
        <div className="dimension-card__section">
          <label className="attr-label">
            <span>start_time:</span>
            <input
              type="time"
              value={point.startTime ?? ""}
              onChange={(e) => onUpdate(point.id, "startTime", e.target.value || undefined)}
              style={{ width: "5.5em" }}
            />
          </label>
          <label className="attr-label" style={{ marginTop: 4 }}>
            <span>duration_minutes:</span>
            <input
              type="number"
              min={1}
              value={point.durationMinutes ?? ""}
              onChange={(e) => onUpdate(point.id, "durationMinutes", e.target.value ? parseInt(e.target.value, 10) : undefined)}
              style={{ width: "4.5em" }}
            />
          </label>
        </div>
      )}

      {/* TagRefs: Tag-Zuordnung per Checkbox */}
      {allTags.length > 0 && (
        <div className="dimension-card__section">
          <div className="dimension-card__option-group">
            {allTags.map((tag) => (
              <label key={tag.id} className="dimension-card__option">
                <input
                  type="checkbox"
                  checked={point.tagRefs.includes(tag.id)}
                  onChange={() => toggleTagRef(tag.id)}
                />{" "}
                {tag.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Helper: Perioden generieren ---------- */
/**
 * Erzeugt periodische Punkte zwischen startDateStr und endDateStr.
 * - numberOfDays === 7  => ISO-Kalenderwochen (YYYY-Wxx, Start immer Montag)
 * - sonst               => Datumslabel (DD.MM.) ab gewähltem Startdatum
 * und erzeugt die Points für time.period.
 * Der erste Punkt ist immer ein Standard-Template (template=true).
 */
function generatePeriodPoints(
  dim: Dimension,
  startDateStr: string,
  endDateStr: string,
  numberOfDays: number,
): Dimension["points"] {
  const parseIsoDateToUTCNoon = (s: string): Date => {
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
    if (!m) return new Date(NaN);
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10) - 1;
    const d = parseInt(m[3], 10);
    // UTC noon avoids edge-cases around timezone/DST while preserving day arithmetic
    return new Date(Date.UTC(y, mo, d, 12, 0, 0));
  };
  const getISOWeekYear = (d: Date): { week: number; year: number } => {
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return { week, year: tmp.getUTCFullYear() };
  };
  const getMondayOfISOWeek = (year: number, week: number): Date => {
    const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
    const dow = simple.getUTCDay() || 7;
    if (dow <= 4) simple.setUTCDate(simple.getUTCDate() - dow + 1);
    else simple.setUTCDate(simple.getUTCDate() + 8 - dow);
    return simple;
  };
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const addDays = (d: Date, days: number) => {
    const next = new Date(d.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };
  const labelFromDate = (d: Date) => `${pad2(d.getUTCDate())}.${pad2(d.getUTCMonth() + 1)}.`;

  const start = parseIsoDateToUTCNoon(startDateStr);
  const end = parseIsoDateToUTCNoon(endDateStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];

  const periodDays = Number.isFinite(numberOfDays) && numberOfDays > 0
    ? Math.floor(numberOfDays)
    : 7;
  const useCalendarWeeks = periodDays === 7;

  const { week: startWeek, year: startYear } = getISOWeekYear(start);
  const firstStart = useCalendarWeeks ? getMondayOfISOWeek(startYear, startWeek) : new Date(start.getTime());
  const newPoints: Dimension["points"] = [];

  let currentStart = new Date(firstStart);
  for (let i = 0; i < 200; i++) {
    if (currentStart > end) break;
    const weekInfo = getISOWeekYear(currentStart);
    const weekLabel = `${weekInfo.year}-W${pad2(weekInfo.week)}`;
    const pointName = useCalendarWeeks ? weekLabel : labelFromDate(currentStart);
    const pointId = useCalendarWeeks ? `week_${weekLabel}` : `period_${toISO(currentStart)}`;

    newPoints.push({
      id: pointId,
      name: pointName,
      tagRefs: [],
      visual: { type: "default" },
      startDate: toISO(currentStart),
    });
    currentStart = addDays(currentStart, periodDays);
  }
  return newPoints;
}

/* ---------- LanesInput – eigene Komponente damit useState erlaubt ist ---------- */
function LanesInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [raw, setRaw] = useState<string>(String(value));
  const parsed = parseInt(raw, 10);
  const isInvalid = !raw || isNaN(parsed) || parsed < 1;

  // Nur synchronisieren wenn sich der gespeicherte Wert von außen ändert
  const prevValueRef = useRef<number>(value);
  useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setRaw(String(value));
    }
  }, [value]);

  return (
    <div style={{ marginBottom: 8 }}>
      <label className="attr-label">
        <span>number_of_lanes=</span>
        <input
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={(e) => setRaw(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={() => {
            const v = parseInt(raw, 10);
            if (!raw || isNaN(v) || v < 1) {
              setRaw(String(value)); // zurücksetzen
            } else {
              onChange(v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          style={{ width: "4em", outline: isInvalid ? "2px solid #e55" : undefined }}
        />
        {isInvalid && <span style={{ color: "#e55" }}>≥ 1</span>}
      </label>
    </div>
  );
}

/* ---------- Sortable Dimension Card (eigene Komponente – Hook-sicher) ---------- */
/**
 * Eine Dimension-Karte:
 * - sortierbar in der Liste aller Dimensionen
 * - Kopf: Name + Typ + Löschen, Expand/Collapse
 * - Inhalt (wenn expanded):
 *   - Name-Input
 *   - Typ-Auswahl
 *   - Liste der Tags (SortableTagCard)
 *   - Liste der Points (SortablePointCard)
 */
function SortableDimensionCard({
  dim,
  allNames,
  allDimensions,
  expandedId,
  setExpandedId,
  updateDimension,
  updateDimensionFields,
  deleteDimension,
  userHue,
  mode,
  sensors,
  relations,
  conditions,
  onRelationsChange,
  onConditionsChange,
  editorRef,
  projectId,
  syntaxStat,
}: {
  dim: Dimension;
  allNames: string[];
  allDimensions: Dimension[];
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  updateDimension: (id: string, field: keyof Dimension, value: any) => void;
  updateDimensionFields: (id: string, fields: Partial<Dimension>) => void;
  deleteDimension: (id: string) => void;
  userHue: number;
  mode: Mode;
  sensors: ReturnType<typeof useSensors>;
  relations: Relation[];
  conditions: ExtendedCondition[];
  onRelationsChange: (rels: Relation[]) => void;
  onConditionsChange: (conds: ExtendedCondition[]) => void;
  editorRef: React.RefObject<HTMLDivElement | null>;
  projectId?: number;
  syntaxStat?: { syntax?: string; SyntaxMessages: string[] };
}) {
  // Dimension selbst ist sortierbar in der Dimensionen-Liste
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dim.id });

  // Aktuell gezogenes Tag / Point (für DragOverlay)
  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  const [activePointId, setActivePointId] = useState<string | null>(null);

  const isExpanded = expandedId === dim.id;
  const isDuplicate = allNames.filter((n) => n === dim.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);
  
  const errorMessage = syntaxStat?.SyntaxMessages && syntaxStat.SyntaxMessages.length > 0 
    ? syntaxStat.SyntaxMessages.join(" | ") 
    : null;
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  // Nächste globale Nummer für Tag/Point (über alle Dimensionen hinweg)
  const nextTagNumber = (() => {
    let max = 0;
    for (const d of allDimensions) {
      for (const t of d.tags) {
        const m = t.name.match(/^Tag(\d+)$/);
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
    }
    return max + 1;
  })();

  const nextPointNumber = (() => {
    let max = 0;
    for (const d of allDimensions) {
      for (const p of d.points) {
        const m = p.name.match(/^Point(\d+)$/);
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
    }
    return max + 1;
  })();

  /**
   * Tag sicher löschen mit Dependency-Check
   */
  const deleteTagSafely = (tagId: string) => {
    const tag = dim.tags.find((t) => t.id === tagId);
    if (!tag) return;

    // Find dependencies
    const affectedRelations = relations.filter((rel) =>
      rel.targets.some((t) =>
        t.dimensionId === dim.id &&
        t.tagIds.includes(tagId)
      )
    );
    const affectedConditions = conditions.filter((cond) => {
      const inDimrefs = cond.dimrefs.some(
        (dr) => dr.dimensionId === dim.id && dr.mode === "tags" && dr.tagIds.includes(tagId)
      );
      const inParticipants = cond.participants?.some((p) =>
        p.dimrefs.some((dr: any) => dr.dimensionId === dim.id && dr.mode === "tags" && dr.tagIds?.includes(tagId))
      );
      const inOn = cond.on?.dimrefs.some((dr: any) => dr.dimensionId === dim.id && dr.mode === "tags" && dr.tagIds?.includes(tagId));
      return inDimrefs || inParticipants || inOn;
    });

    // Points, die dieses Tag referenzieren
    const affectedPoints = dim.points.filter((p) => p.tagRefs.includes(tagId));

    if (affectedRelations.length > 0 || affectedConditions.length > 0 || affectedPoints.length > 0) {
      const relationNames = affectedRelations.map((r) => r.name).join(", ");
      const conditionNames = affectedConditions.map((c) => c.name).join(", ");
      const pointNames = affectedPoints.map((p) => p.name).join(", ");

      const message = `The tag "${tag.name}" is currently used in:\n\n` +
        (affectedRelations.length > 0
          ? `- ${affectedRelations.length} Relation(s): ${relationNames} (will be cleaned up or deleted if empty)\n`
          : "") +
        (affectedConditions.length > 0
          ? `- ${affectedConditions.length} Condition(s): ${conditionNames} (tag reference will be removed)\n`
          : "") +
        (affectedPoints.length > 0
          ? `- ${affectedPoints.length} Point(s): ${pointNames}\n`
          : "") +
        `\nDo you want to continue?`;

      if (!window.confirm(message)) {
        return;
      }

      // Cleanup Relations: tag aus Targets entfernen, leere Targets/Relationen löschen
      const updatedRelations = relations
        .map((rel) => ({
          ...rel,
          targets: rel.targets.map((t) =>
            t.dimensionId === dim.id
              ? { ...t, tagIds: t.tagIds.filter((id) => id !== tagId) }
              : t
          ).filter((t) =>
            !(t.dimensionId === dim.id && t.mode === "tags" && t.tagIds.length === 0)
          ),
        }))
        .filter((rel) => rel.targets.length > 0);
      onRelationsChange(updatedRelations);

      // Cleanup Conditions: tagId aus dimrefs entfernen, leere dimrefs entfernen,
      // Condition löschen wenn komplett leer
      const cleanDimrefs = (dimrefs: any[]) =>
        dimrefs
          .map((dr: any) =>
            dr.dimensionId === dim.id && dr.mode === "tags"
              ? { ...dr, tagIds: (dr.tagIds ?? []).filter((id: string) => id !== tagId) }
              : dr
          )
          .filter((dr: any) =>
            !(dr.dimensionId === dim.id && dr.mode === "tags" && dr.tagIds.length === 0)
          );

      const updatedConditions = conditions
        .map((cond) => ({
          ...cond,
          dimrefs: cleanDimrefs(cond.dimrefs),
          participants: cond.participants?.map((p: any) => ({ ...p, dimrefs: cleanDimrefs(p.dimrefs) })),
          on: cond.on ? { ...cond.on, dimrefs: cleanDimrefs(cond.on.dimrefs) } : cond.on,
        }))
        .filter((cond) => {
          // Condition behalten wenn noch dimrefs vorhanden, oder sync mit mindestens einem participant mit dimrefs
          if (cond.dimrefs.length > 0) return true;
          if (cond.participants && cond.participants.some((p: any) => p.dimrefs.length > 0)) return true;
          if (cond.on && cond.on.dimrefs.length > 0) return true;
          return false;
        });
      onConditionsChange(updatedConditions);

      // Cleanup Points (tag reference entfernen)
      const updatedPoints = dim.points.map((p) => ({
        ...p,
        tagRefs: p.tagRefs.filter((id) => id !== tagId),
      }));
      updateDimension(dim.id, "points", updatedPoints);
    }

    // Delete the tag
    updateDimension(
      dim.id,
      "tags",
      dim.tags.filter((t) => t.id !== tagId)
    );
  };

  /**
   * Point sicher löschen mit Dependency-Check
   */
  const deletePointSafely = (pointId: string) => {
    const point = dim.points.find((p) => p.id === pointId);
    if (!point) return;

    // Find dependencies
    const affectedRelations = relations.filter((rel) =>
      rel.targets.some((t) =>
        t.dimensionId === dim.id &&
        t.pointIds.includes(pointId)
      )
    );
    const affectedConditions = conditions.filter((cond) => {
      const inDimrefs = cond.dimrefs.some(
        (dr) => dr.dimensionId === dim.id && dr.mode === "points" && dr.pointIds.includes(pointId)
      );
      const inParticipants = cond.participants?.some((p) =>
        p.dimrefs.some((dr: any) => dr.dimensionId === dim.id && dr.mode === "points" && dr.pointIds?.includes(pointId))
      );
      const inOn = cond.on?.dimrefs.some((dr: any) => dr.dimensionId === dim.id && dr.mode === "points" && dr.pointIds?.includes(pointId));
      return inDimrefs || inParticipants || inOn;
    });

    if (affectedRelations.length > 0 || affectedConditions.length > 0) {
      const relationNames = affectedRelations.map((r) => r.name).join(", ");
      const conditionNames = affectedConditions.map((c) => c.name).join(", ");

      const message = `The point "${point.name}" is currently used in:\n\n` +
        (affectedRelations.length > 0
          ? `- ${affectedRelations.length} Relation(s): ${relationNames} (will be cleaned up or deleted if empty)\n`
          : "") +
        (affectedConditions.length > 0
          ? `- ${affectedConditions.length} Condition(s): ${conditionNames} (point reference will be removed)\n`
          : "") +
        `\nDo you want to continue?`;

      if (!window.confirm(message)) {
        return;
      }

      // Cleanup Relations: point aus Targets entfernen, leere Targets/Relationen löschen
      const updatedRelations = relations
        .map((rel) => ({
          ...rel,
          targets: rel.targets.map((t) =>
            t.dimensionId === dim.id
              ? { ...t, pointIds: t.pointIds.filter((id: string) => id !== pointId) }
              : t
          ).filter((t) =>
            !(t.dimensionId === dim.id && t.mode === "points" && t.pointIds.length === 0)
          ),
        }))
        .filter((rel) => rel.targets.length > 0);
      onRelationsChange(updatedRelations);

      // Cleanup Conditions: pointId aus dimrefs entfernen, leere dimrefs entfernen,
      // Condition löschen wenn komplett leer
      const cleanDimrefs = (dimrefs: any[]) =>
        dimrefs
          .map((dr: any) =>
            dr.dimensionId === dim.id && dr.mode === "points"
              ? { ...dr, pointIds: (dr.pointIds ?? []).filter((id: string) => id !== pointId) }
              : dr
          )
          .filter((dr: any) =>
            !(dr.dimensionId === dim.id && dr.mode === "points" && dr.pointIds.length === 0)
          );

      const updatedConditions = conditions
        .map((cond) => ({
          ...cond,
          dimrefs: cleanDimrefs(cond.dimrefs),
          participants: cond.participants?.map((p: any) => ({ ...p, dimrefs: cleanDimrefs(p.dimrefs) })),
          on: cond.on ? { ...cond.on, dimrefs: cleanDimrefs(cond.on.dimrefs) } : cond.on,
        }))
        .filter((cond) => {
          if (cond.dimrefs.length > 0) return true;
          if (cond.participants && cond.participants.some((p: any) => p.dimrefs.length > 0)) return true;
          if (cond.on && cond.on.dimrefs.length > 0) return true;
          return false;
        });
      onConditionsChange(updatedConditions);
    }

    // Delete the point
    updateDimension(
      dim.id,
      "points",
      dim.points.filter((p) => p.id !== pointId)
    );
  };

  return (
    <div
      ref={setNodeRef}
      className="dimension-card"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: getBaseColor(3, "dimension", userHue, mode),
      }}
    >
      {/* Header der Dimension: Drag-Handle, Arrow, Name, Löschen */}
      <div className="dimension-card__header">
        {/* Drag-Handle */}
        <div {...attributes} {...listeners} className="dimension-card__drag" title="Drag to reorder · Drag out to remove">
          ☰
        </div>

        {/* Toggle Arrow - nur dieser ist klickbar für Expand/Collapse */}
        <span
          style={{ cursor: "pointer", padding: "0 8px", userSelect: "none" }}
          onClick={() => setExpandedId(isExpanded ? null : dim.id)}
        >
          {isExpanded ? "▾" : "▸"}
        </span>

        {/* Name - Doppelklick zum Editieren */}
        {isEditingName ? (
          <>
            <input
              type="text"
              value={dim.name}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                updateDimension(dim.id, "name", stripped);
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e) => e.target.select()}
              className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
              style={{ flex: 1 }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em", whiteSpace: "nowrap" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </>
        ) : (
          <div style={{ flex: 1, userSelect: "none" }} onDoubleClick={() => setIsEditingName(true)}>
            {dim.name}
          </div>
        )}
        {/* Syntax Validation Warning */}
        {errorMessage && (
          <span title={errorMessage} style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}>
            {validationIcon}
          </span>
        )}
        {/* Dimension löschen – entfernt per Drag-out */}
      </div>

      {/* Duplikatswarnung direkt nach Header */}
      {isDuplicate && (
        <div className="dimension-card__warning">
          Name already in use
        </div>
      )}

      {/* Detailbereich der Dimension */}
      {isExpanded && (
        <div style={{ marginTop: 8 }}>
          {/* Typ-Auswahl */}
          <div style={{ marginBottom: 8 }}>
            <label>
              Type:{" "}
              <select
                value={dim.dimType ?? "none"}
                onChange={(e) =>
                  updateDimension(dim.id, "dimType", e.target.value as DimensionType)
                }
              >
                <option value="none">none</option>
                <option value="time.day">time.day</option>
                <option value="time.slot">time.slot</option>
                <option value="time.period">time.period</option>
                <option value="resource.team">resource.team</option>
                <option value="resource.person">resource.person</option>
                <option value="resource.place">resource.place</option>
                <option value="resource.asset">resource.asset</option>
                <option value="topic">topic</option>
              </select>
            </label>
          </div>

          {/* time.period: Start/End-Datum → KWs werden sofort automatisch generiert */}
          {dim.dimType === "time.period" && (
            <div style={{ marginBottom: 10, padding: "8px", background: "rgba(0,0,0,0.06)", borderRadius: 5 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span>Start:</span>
                  <input
                    type="date"
                    value={dim.startDate ?? ""}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      let effectiveEnd = dim.endDate ?? "";
                      if (newStartDate && !effectiveEnd) {
                        const d = new Date(newStartDate);
                        d.setFullYear(d.getFullYear() + 1);
                        effectiveEnd = d.toISOString().slice(0, 10);
                      }
                      const effectiveNumberOfDays = dim.numberOfDays ?? 7;
                      const newPoints = (newStartDate && effectiveEnd)
                        ? generatePeriodPoints(dim, newStartDate, effectiveEnd, effectiveNumberOfDays)
                        : dim.points;
                      updateDimensionFields(dim.id, {
                        startDate: newStartDate,
                        endDate: effectiveEnd || dim.endDate,
                        numberOfDays: effectiveNumberOfDays,
                        points: newPoints,
                      });
                    }}
                    style={{ fontSize: "0.9em" }}
                  />
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span>End:</span>
                  <input
                    type="date"
                    value={dim.endDate ?? ""}
                    onChange={(e) => {
                      const newEndDate = e.target.value;
                      const effectiveStart = dim.startDate ?? "";
                      const effectiveNumberOfDays = dim.numberOfDays ?? 7;
                      const newPoints = (effectiveStart && newEndDate)
                        ? generatePeriodPoints(dim, effectiveStart, newEndDate, effectiveNumberOfDays)
                        : dim.points;
                      updateDimensionFields(dim.id, {
                        endDate: newEndDate,
                        numberOfDays: effectiveNumberOfDays,
                        points: newPoints,
                      });
                    }}
                    style={{ fontSize: "0.9em" }}
                  />
                </label>
                <label className="attr-label" style={{ gap: 4 }}>
                  <span>number_of_days:</span>
                  <input
                    type="number"
                    min={1}
                    value={dim.numberOfDays ?? 7}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      const newNumberOfDays = Number.isFinite(parsed) && parsed > 0 ? parsed : 7;
                      const effectiveStart = dim.startDate ?? "";
                      const effectiveEnd = dim.endDate ?? "";
                      const newPoints = (effectiveStart && effectiveEnd)
                        ? generatePeriodPoints(dim, effectiveStart, effectiveEnd, newNumberOfDays)
                        : dim.points;
                      updateDimensionFields(dim.id, {
                        numberOfDays: newNumberOfDays,
                        points: newPoints,
                      });
                    }}
                    style={{ fontSize: "0.9em", width: "5em" }}
                  />
                </label>
                <label className="attr-label" style={{ gap: 4 }}>
                  <span>lookahead:</span>
                  <input
                    type="number"
                    min={0}
                    value={dim.lookahead ?? ""}
                    onChange={(e) =>
                      updateDimension(dim.id, "lookahead", e.target.value !== "" ? parseInt(e.target.value, 10) : undefined)
                    }
                    style={{ fontSize: "0.9em", width: "4em" }}
                  />
                </label>
                <label className="attr-label" style={{ gap: 4 }}>
                  <span>publish:</span>
                  <input
                    type="number"
                    min={0}
                    value={dim.publish ?? ""}
                    onChange={(e) =>
                      updateDimension(dim.id, "publish", e.target.value !== "" ? parseInt(e.target.value, 10) : undefined)
                    }
                    style={{ fontSize: "0.9em", width: "4em" }}
                  />
                </label>
              </div>
            </div>
          )}

          {/* time.slot: Anzahl sichtbarer Karten pro Zeitslot */}
          {dim.dimType === "time.slot" && (
            <LanesInput
              value={dim.calendarMaxLanes ?? 1}
              onChange={(v) => updateDimension(dim.id, "calendarMaxLanes", v)}
            />
          )}

          {/* Tags-Abschnitt */}
          <div style={{ marginTop: 10 }}>
            <strong>Tags</strong>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e: DragStartEvent) => setActiveTagId(String(e.active.id))}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                setActiveTagId(null);

                // Drag-out-to-delete: Element komplett außerhalb → löschen
                const finalRect = active.rect.current.translated ?? active.rect.current.initial;
                const editorRect = editorRef.current?.getBoundingClientRect();
                if (
                  finalRect && editorRect &&
                  (finalRect.right <= editorRect.left ||
                    finalRect.left >= editorRect.right ||
                    finalRect.bottom <= editorRect.top ||
                    finalRect.top >= editorRect.bottom)
                ) {
                  deleteTagSafely(String(active.id));
                  return;
                }

                if (active.id !== over?.id) {
                  const oldIndex = dim.tags.findIndex((t) => t.id === active.id);
                  const newIndex = dim.tags.findIndex((t) => t.id === over?.id);
                  updateDimension(dim.id, "tags", arrayMove(dim.tags, oldIndex, newIndex));
                }
              }}
            >
              <SortableContext
                items={dim.tags.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {dim.tags.map((tag) => (
                  <SortableTagCard
                    key={tag.id}
                    tag={tag}
                    allNames={allNames}
                    onDelete={deleteTagSafely}
                    onUpdate={(tagId, field, value) =>
                      updateDimension(
                        dim.id,
                        "tags",
                        dim.tags.map((t) =>
                          t.id === tagId ? { ...t, [field]: value } : t
                        )
                      )
                    }
                    userHue={userHue}
                    mode={mode}
                  />
                ))}
              </SortableContext>

              {/* DragOverlay für Tag */}
              <DragOverlay>
                {activeTagId ? (
                  <div className="drag-overlay-card dimension-card" style={{
                    background: applyVisuals([dim.tags.find(t => t.id === activeTagId)?.visual ?? { type: "default" }], mode),
                  }}>
                    <div className="editor-card__drag">☰</div>
                    <div>{dim.tags.find(t => t.id === activeTagId)?.name ?? ""}</div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            {/* Tag hinzufügen */}
            <button
              onClick={() =>
                updateDimension(dim.id, "tags", [
                  ...dim.tags,
                  {
                    id: Date.now().toString(),
                    name: "Tag" + nextTagNumber,
                    visual: { type: "default" },
                    fix: false,
                  },
                ])
              }
            >
              + Tag
            </button>
          </div>

          {/* Points-Abschnitt mit DnD */}
          <div style={{ marginTop: 10 }}>
            <strong>Points</strong>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e: DragStartEvent) => setActivePointId(String(e.active.id))}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                setActivePointId(null);

                // Drag-out-to-delete: Element komplett außerhalb → löschen
                const finalRect = active.rect.current.translated ?? active.rect.current.initial;
                const editorRect = editorRef.current?.getBoundingClientRect();
                if (
                  finalRect && editorRect &&
                  (finalRect.right <= editorRect.left ||
                    finalRect.left >= editorRect.right ||
                    finalRect.bottom <= editorRect.top ||
                    finalRect.top >= editorRect.bottom)
                ) {
                  deletePointSafely(String(active.id));
                  return;
                }

                if (active.id !== over?.id) {
                  const oldIndex = dim.points.findIndex((p) => p.id === active.id);
                  const newIndex = dim.points.findIndex((p) => p.id === over?.id);
                  updateDimension(dim.id, "points", arrayMove(dim.points, oldIndex, newIndex));
                }
              }}
            >
              <SortableContext
                items={dim.points.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                {dim.points.map((point) => (
                  <SortablePointCard
                    key={point.id}
                    point={point}
                    allTags={dim.tags}
                    allNames={dim.points.map((p) => p.name)}
                    onDelete={deletePointSafely}
                    onUpdate={(pid, field, value) => {
                      const updatedPoints = dim.points.map((p) =>
                        p.id === pid ? { ...p, [field]: value } : p
                      );
                      // time.period: falls startDate eines Points nach dem End-Datum liegt
                      // → End-Datum automatisch auf dieses Datum ausdehnen
                      if (
                        dim.dimType === "time.period" &&
                        field === "startDate" &&
                        typeof value === "string" &&
                        value &&
                        dim.endDate &&
                        value > dim.endDate
                      ) {
                        updateDimensionFields(dim.id, {
                          points: updatedPoints,
                          endDate: value,
                        });
                      } else {
                        updateDimension(dim.id, "points", updatedPoints);
                      }
                    }}
                    userHue={userHue}
                    mode={mode}
                    isTimeWeek={dim.dimType === "time.period"}
                    isTimeDay={dim.dimType === "time.day"}
                    isTimeSlot={dim.dimType === "time.slot"}
                    isResource={typeof dim.dimType === "string" && dim.dimType.startsWith("resource.")}
                    dimName={dim.name}
                    projectId={projectId}
                  />
                ))}
              </SortableContext>

              {/* DragOverlay für Point */}
              <DragOverlay>
                {activePointId ? (
                  <div className="drag-overlay-card dimension-card" style={{
                    background: applyVisuals([dim.points.find(p => p.id === activePointId)?.visual ?? { type: "default" }], mode),
                  }}>
                    <div className="editor-card__drag">☰</div>
                    <div>{dim.points.find(p => p.id === activePointId)?.name ?? ""}</div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>

            {/* Point hinzufügen */}
            <button
              onClick={() =>
                updateDimension(dim.id, "points", [
                  ...dim.points,
                  {
                    id: Date.now().toString(),
                    name: "Point" + nextPointNumber,
                    tagRefs: [],
                    visual: { type: "default" },
                    fix: false,
                  },
                ])
              }
            >
              + Point
            </button>
          </div>
          
          {/* Validation Error Banner at Bottom */}
          {errorMessage && (
            <div className="syntax-error-banner" style={{ marginTop: 12 }}>
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Hauptkomponente: DimensionEditor ---------- */
/**
 * Top-Level Editor für alle Dimensionen:
 * - Liste der Dimensionen, sortierbar per Drag & Drop
 * - jede Dimension über SortableDimensionCard dargestellt
 * - kann ein- und ausgeklappt werden (isActive/toggle)
 * - bietet "+ Dimension" zum Hinzufügen
 */
export default function DimensionEditor({
  dimensions,
  onChange,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
  relations,
  conditions,
  onRelationsChange,
  onConditionsChange,
  syntaxData,
}: {
  dimensions: Dimension[];
  onChange: (dims: Dimension[]) => void;
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  relations: Relation[];
  conditions: ExtendedCondition[];
  onRelationsChange: (rels: Relation[]) => void;
  onConditionsChange: (conds: ExtendedCondition[]) => void;
  syntaxData?: SyntaxchecksData | null;
}) {
  const dimensionSyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax?: string; SyntaxMessages: string[] }>();
    const arr = Array.isArray(syntaxData) ? syntaxData : [];
    arr.forEach((entry: any) => {
      const name = entry?.dimensionName;
      if (name) {
        if (!map.has(name)) {
          map.set(name, { syntax: entry.syntax, SyntaxMessages: [] });
        }
        if (entry.message) {
          map.get(name)!.SyntaxMessages.push(entry.message);
        }
      }
    });
    return map;
  }, [syntaxData]);

  // Welche Dimension ist aktuell aufgeklappt?
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Aktuell gezogene Dimension (für DragOverlay)
  const [activeDimId, setActiveDimId] = useState<string | null>(null);

  // Ref auf den äußeren Editor-Container für Drag-out-to-delete
  const editorFrameRef = useRef<HTMLDivElement | null>(null);

  // Gemeinsame DnD-Sensoren für alle Dimensionen/Tags/Points
  // MouseSensor: Aktiviert bei 10px Bewegung, um Klicks zu erlauben
  // TouchSensor: Aktiviert bei 5px Bewegung und 250ms Delay, um Scrollen zu unterscheiden
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fix: Wenn Maus schnell aus dem Fenster geht, verliert dnd-kit den mouseup-Event.
  // Wir dispatchen einen synthetischen mouseup, damit dnd-kit den Drag sauber beendet.
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      }
    };
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => window.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  /**
   * Dimension updaten:
   * - mappt über die Liste, sucht nach id
   * - updated nur das eine Objekt (spread + Feld)
   * - Arrays werden kopiert, um Immutability zu wahren
   */
  const updateDimension = (
    id: string,
    field: keyof Dimension,
    value: any
  ) => {
    onChange(
      dimensions.map((d) =>
        d.id === id
          ? { ...d, [field]: Array.isArray(value) ? [...value] : value }
          : d
      )
    );

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  // Mehrere Felder einer Dimension in einem einzigen onChange-Aufruf aktualisieren
  const updateDimensionFields = (id: string, fields: Partial<Dimension>) => {
    onChange(
      dimensions.map((d) => {
        if (d.id !== id) return d;
        return { ...d, ...fields } as Dimension;
      })
    );
    notifyProjectActivity(projectId).catch(() => { });
  };

  /**
   * Neue Dimension hinzufügen:
   * - Standardname "DimensionName"
   * - leere Tags/Points-Arrays
   * - neue Dimension an Liste anhängen
   * - sofort aufklappen
   */
  const addDimension = () => {
    const newDim: Dimension = {
      id: Date.now().toString(),
      name: "Dimension" + (dimensions.length + 1),
      dimType: "none",
      tags: [],
      points: [],
    };
    onChange([...dimensions, newDim]);
    setExpandedId(newDim.id);

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  /**
   * Dimension löschen:
   * - prüft ob Dimension in Relations oder Conditions verwendet wird
   * - warnt User und bereinigt Referenzen falls bestätigt
   * - löscht dann die Dimension
   */
  const deleteDimension = (id: string) => {
    const dim = dimensions.find((d) => d.id === id);
    if (!dim) return;

    // Find dependencies
    const affectedRelations = relations.filter((rel) =>
      rel.targets.some((t) => t.dimensionId === id)
    );
    const affectedConditions = conditions.filter((cond) => {
      const inDimrefs = cond.dimrefs.some((dr) => dr.dimensionId === id);
      const inParticipants = cond.participants?.some((p) =>
        p.dimrefs.some((dr: any) => dr.dimensionId === id)
      );
      const inOn = cond.on?.dimrefs.some((dr: any) => dr.dimensionId === id);
      return inDimrefs || inParticipants || inOn;
    });

    // Check if there are dependencies
    if (affectedRelations.length > 0 || affectedConditions.length > 0) {
      const relationNames = affectedRelations.map((r) => r.name).join(", ");
      const conditionNames = affectedConditions.map((c) => c.name).join(", ");

      const message = `The dimension "${dim.name}" is currently used in:\n\n` +
        (affectedRelations.length > 0
          ? `- ${affectedRelations.length} Relation(s): ${relationNames} (dimension reference will be removed)\n`
          : "") +
        (affectedConditions.length > 0
          ? `- ${affectedConditions.length} Condition(s): ${conditionNames} (dimension reference will be removed)\n`
          : "") +
        `\nDo you want to continue?`;

      if (!window.confirm(message)) {
        return; // User cancelled
      }

      // Cleanup Relations: Targets der gelöschten Dimension entfernen, leere Relations löschen
      const updatedRelations = relations
        .map((rel) => ({
          ...rel,
          targets: rel.targets.filter((t) => t.dimensionId !== id),
        }))
        .filter((rel) => rel.targets.length > 0);

      onRelationsChange(updatedRelations);

      // Cleanup Conditions: dimrefs der gelöschten Dimension entfernen,
      // Condition löschen wenn komplett leer
      const cleanDimrefs = (dimrefs: any[]) =>
        dimrefs.filter((dr: any) => dr.dimensionId !== id);

      const updatedConditions = conditions
        .map((cond) => ({
          ...cond,
          dimrefs: cleanDimrefs(cond.dimrefs),
          participants: cond.participants?.map((p: any) => ({ ...p, dimrefs: cleanDimrefs(p.dimrefs) })),
          on: cond.on ? { ...cond.on, dimrefs: cleanDimrefs(cond.on.dimrefs) } : cond.on,
        }))
        .filter((cond) => {
          if (cond.dimrefs.length > 0) return true;
          if (cond.participants && cond.participants.some((p: any) => p.dimrefs.length > 0)) return true;
          if (cond.on && cond.on.dimrefs.length > 0) return true;
          return false;
        });

      onConditionsChange(updatedConditions);
    }

    // Delete the dimension
    onChange(dimensions.filter((d) => d.id !== id));
    if (expandedId === id) setExpandedId(null);

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  return (
    <div
      ref={editorFrameRef}
      className="settings-panel"
      style={{
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      {/* Section Header: "Dimensions" mit Ein-/Ausklapp-Logik */}
      <div className="settings-panel__header" onClick={toggle}>
        <div className="settings-panel__title">
          {isActive ? "▾" : "▸"} Dimensions
        </div>
      </div>

      {/* Inhalt nur anzeigen, wenn Section aktiv ist */}
      {isActive && (
        <div className="settings-panel__content">
          {/* DnD-Kontext für Dimensionen-Liste */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setExpandedId(null);
              setActiveDimId(String(e.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveDimId(null);

              // Drag-out-to-delete: Element komplett außerhalb → löschen
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                deleteDimension(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = dimensions.findIndex(
                  (d) => d.id === active.id
                );
                const newIndex = dimensions.findIndex(
                  (d) => d.id === over?.id
                );
                onChange(arrayMove(dimensions, oldIndex, newIndex));
                notifyProjectActivity(projectId).catch(() => { });
              }
            }}
          >
            <SortableContext
              items={dimensions.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              {dimensions.map((dim) => (
                <SortableDimensionCard
                  key={dim.id}
                  dim={dim}
                  allNames={allNames}
                  allDimensions={dimensions}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  updateDimension={updateDimension}
                  updateDimensionFields={updateDimensionFields}
                  deleteDimension={deleteDimension}
                  userHue={userHue}
                  mode={mode}
                  sensors={sensors}
                  relations={relations}
                  conditions={conditions}
                  onRelationsChange={onRelationsChange}
                  onConditionsChange={onConditionsChange}
                  editorRef={editorFrameRef}
                  projectId={projectId}
                  syntaxStat={dimensionSyntaxMap.get(dim.name)}
                />
              ))}
            </SortableContext>

            {/* DragOverlay: damit dnd-kit translated-Rect korrekt tracked */}
            <DragOverlay>
              {activeDimId ? (
                <div className="drag-overlay-card dimension-card" style={{
                  background: getBaseColor(3, "dimension", userHue, mode),
                }}>
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>
                    {dimensions.find(d => d.id === activeDimId)?.name ?? ""}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* Button: neue Dimension hinzufügen */}
          <button onClick={addDimension} style={{ marginTop: 10 }}>
            + Dimension
          </button>
        </div>
      )}
    </div>
  );
}
